import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CheckIcon from '@mui/icons-material/Check';
import ErrorIcon from '@mui/icons-material/Error';

import { PromotionCard } from '../../account/promotion/PromotionCard';
import { usePromotion } from '../../account/promotion/PromotionContext';
import { Steam } from '../../account/providers/steam/Steam';
import { GameAccount } from '../../authentication/Authentication.model';
import { useAuth } from '../../authentication/AuthenticationContext';
import { RequestError } from '../../Error.model';
import { ActivatePromotion } from '../Activation.model';

interface Props {
  goToClaim: ()=>void;
  provider: string;
  gameId: string;
  promotions: ActivatePromotion[];
}

export function ActivationLink(props: Props): JSX.Element {
  const { t } = useTranslation(['error', 'translation']);
  const { user, getPlayer } = useAuth();
  const { activate } = usePromotion();

  const [codeError, setCodeError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const getLinkedProvider = (): GameAccount => (
    user.accounts.find((account) => account.type === props.provider)
  );

  const activateGame = async (providerLinked: GameAccount): Promise<void> => {
    setIsLoading(true);
    setCodeError(null);
    try {
      await activate(props.provider, providerLinked.id, props.gameId);
      await getPlayer();
      props.goToClaim();
    } catch (e) {
      const err = e as RequestError;

      if (err.code === 'GameNotOwned') {
        setCodeError(err.code);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const validateGameOwnership = (): void => {
    const providerLinked = getLinkedProvider();

    if (providerLinked) {
      activateGame(providerLinked);
    }
  };

  const ProviderToLink = useCallback((): JSX.Element => {
    switch (props.provider) {
      case 'steam':
        return <Steam />;
      default:
        return <></>;
    }
  }, [props.provider]);

  useEffect(() => {
    validateGameOwnership();
  }, []);

  return (
    <>
      <h3>
        {`${t('translation:activation.welcome')} ${user.nickName}`}
      </h3>

      <h2 className='activation-title'>
        {t(
          'translation:activation.title',
          {
            requiredGame: t(`translation:games.${props.gameId}`),
            rewardGame: t(`translation:games.${props.promotions[0]?.relatedToGameId}`),
          },
        )}
      </h2>

      <div className='activation-content'>
        {props.promotions.map((promotion) => (
          <PromotionCard
            key={promotion.promotionId}
            activatePromotion={promotion}
          />
        )) }
      </div>

      <div className='activation-description'>
        <div className='question-circle-container activation-wizard'>
          <div className='question-mobile-container'>
            <div className='question-circle-node'>
              <div className='question-circle' data-content={t('translation:activation.stepLogin')}>
                <CheckIcon />
              </div>
              <div className='question-line' />
            </div>
          </div>
          <div className='question-mobile-container'>
            <div className='question-circle-node'>
              <div className='question-circle' data-content={t('translation:activation.stepActivation')}>
                <CheckIcon />
              </div>
              <div className='question-line in-progress' />
            </div>
          </div>
          <div className='question-mobile-container'>
            <div className='question-circle-node'>
              <div className='question-circle disabled' data-content={t('translation:activation.stepClaim')}>
                <div className='check' />
              </div>
            </div>
          </div>
        </div>

        {isLoading
          ? <span className='dbd-loader' />
          : ProviderToLink() }
        {codeError
          ? (
            <div className='activation-error' data-cy='activation-error-label'>
              <ErrorIcon />
              {codeError === 'GameNotOwned'
                ? t(`error:${codeError}`, { requiredGame: t(`translation:games.${props.gameId}`) })
                : t(`error:${codeError}`)}
            </div>
          )
          : (
            <div className='content'>
              {t('translation:activation.descriptionLink', { requiredGame: t(`translation:games.${props.gameId}`) })}
            </div>
          )}

        <button
          type='button'
          className='btn btn-primary-game'
          onClick={validateGameOwnership}
          disabled={!getLinkedProvider()}
          data-cy='activation-activate'
        >
          {t('translation:button.next')}
        </button>
      </div>
    </>
  );
}
